import { useState, useCallback, RefObject } from 'react';
import { Status1 } from '../types';
/**
 * TODO: Testing & Hardening Roadmap
 *
 * Note to future self: When ready to implement testing, give Claude this prompt:
 *
 * "I'm ready to implement testing for the keyboard navigation in my milestone map project.
 * Previously, we enhanced useKeyboardNavigation with error handling, debug logging, and
 * keyboard shortcuts. Here are the specific implementations we planned:
 *
 * KeyboardAction type for test scenarios:
 * ```typescript
 * type KeyboardAction =
 *   | { type: 'navigate'; direction: 'up' | 'down' | 'left' | 'right' }
 *   | { type: 'status'; code: 'g' | 'y' | 'r' | 'p' | '?' }
 *   | { type: 'activate' }
 *   | { type: 'escape' };
 * ```
 *
 * simulateKeyboardNavigation helper:
 * ```typescript
 * function simulateKeyboardNavigation(
 *   element: HTMLElement,
 *   actions: KeyboardAction[],
 *   startPosition: GridPosition = { row: 0, col: 0 }
 * ) {
 *   // Simulates a sequence of keyboard actions
 * }
 * ```
 *
 * findGridCell utility:
 * ```typescript
 * function findGridCell(
 *   container: HTMLElement,
 *   position: GridPosition
 * ): HTMLElement | null {
 *   return container.querySelector(
 *     `[data-row="${position.row}"][data-col="${position.col}"]`
 *   );
 * }
 * ```
 *
 * Debug panel features we want to add:
 * - Filtering log entries by action type
 * - Search functionality for log content
 * - Collapsible JSON details
 * - Copy log to clipboard
 * - Log persistence across page reloads
 *
 * Performance monitoring features:
 * - Track time between key press and focus update
 * - Monitor unnecessary re-renders
 * - Log performance metrics in debug mode
 * - Warning for slow operations (> 16ms)
 *
 * Could you help me implement these, starting with setting up the testing infrastructure?
 * [rest of prompt remains the same...]
 */
export type GridPosition = {
  row: number;
  col: number;
};

export type GridKeyboardAction =
  | { type: 'NAVIGATE'; direction: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT' }
  | { type: 'ACTIVATE' }
  | { type: 'STATUS_UPDATE'; code: Status1['statusCode'] }
  | { type: 'DELETE' }
  | { type: 'SCROLL'; direction: 'FORWARD' | 'BACK' };

// New validation types
export type ValidationError = {
  code: 'INVALID_POSITION' | 'OUT_OF_BOUNDS' | 'INVALID_KEY_ACTION';
  message: string;
  details?: unknown;
};

// Add to existing types
export type GridSelection = {
  start: GridPosition;
  end: GridPosition;
} | null;

// Define valid status codes using Status1 type
const validStatusCodes: Status1['statusCode'][] = ['g', 'y', 'r', 'p', '?', 'G', 'R', 'Y', ''];

interface UseKeyboardNavigationProps {
  tableRef: RefObject<HTMLTableElement>;
  rowCount: number;
  columnCount: number;
  onFocusChange?: (position: GridPosition) => void;
  onActivate?: (position: GridPosition) => void;
  onKeyPress?: (key: string, position: GridPosition) => void;
  canScrollForward?: boolean;
  canScrollBack?: boolean;
  scrollForward?: () => void;
  scrollBack?: () => void;
  scrollEdges?: {
    left?: number; // The leftmost column that triggers scroll
    right?: number; // The rightmost column that triggers scroll
  };
  debug?: boolean;
  onError?: (error: ValidationError) => void;
  strictValidation?: boolean; // If true, throws errors instead of just logging
}

type UseKeyboardNavigationReturn = {
  focusPosition: GridPosition;
  selection: GridSelection;
  handleCellFocus: (row: number, col: number) => void;
  handleKeyDown: (e: React.KeyboardEvent, row: number, col: number) => void;
  handleSelectionStart: (row: number, col: number) => void;
  handleSelectionEnd: (row: number, col: number) => void;
};

function isValidPosition(
  position: GridPosition,
  rowCount: number,
  columnCount: number,
  allowNegative = true,
): boolean {
  if (!allowNegative && (position.row < 0 || position.col < 0)) {
    return false;
  }

  if (position.row >= rowCount || position.col >= columnCount) {
    return false;
  }

  if (allowNegative) {
    // Allow position -1, -1 to be valid, but not other negative positions.
    // This is useful for cases where the focus is intentionally removed from the grid.
    return position.row === -1 ? position.col === -1 : position.col >= 0;
  }

  return true;
}

// Helper function to get all positions in a selection
export const getSelectedPositions = (sel: GridSelection): GridPosition[] => {
  if (!sel) return [];

  const positions: GridPosition[] = [];
  const startRow = Math.min(sel.start.row, sel.end.row);
  const endRow = Math.max(sel.start.row, sel.end.row);
  const startCol = Math.min(sel.start.col, sel.end.col);
  const endCol = Math.max(sel.start.col, sel.end.col);

  for (let row = startRow; row <= endRow; row++) {
    for (let col = startCol; col <= endCol; col++) {
      positions.push({ row, col });
    }
  }
  return positions;
};

/**
 * Hook to handle keyboard navigation for a grid (e.g. HTML table).
 *
 * @param tableRef - The ref of the table element.
 * @param rowCount - The number of rows in the grid.
 * @param columnCount - The number of columns in the grid.
 * @param onFocusChange - A callback function to handle focus change.
 * @param onActivate - A callback function to handle activation (click/Enter/Space).
 * @param onKeyPress - A callback function to handle key presses.
 * @param canScrollForward - A boolean to check if the user can go forward.
 * @param canScrollBack - A boolean to check if the user can go back.
 * @param scrollForward - A callback function to handle next action.
 * @param scrollBack - A callback function to handle previous action.
 * @param scrollEdges - An object to define custom scroll edges.
 * @param debug - A boolean to enable debug logging.
 * @param onError - A callback function to handle validation errors.
 * @param strictValidation - A boolean to enable strict validation.
 * @returns An object containing the current focus position, a function to set the focus position, and a function to handle key down events.
 */
export function useKeyboardNavigation({
  tableRef,
  rowCount,
  columnCount,
  onFocusChange,
  onActivate,
  onKeyPress,
  canScrollForward,
  canScrollBack,
  scrollForward,
  scrollBack,
  scrollEdges = { left: 0, right: columnCount - 1 },
  debug = false,
  onError,
  strictValidation = false,
}: UseKeyboardNavigationProps): UseKeyboardNavigationReturn {
  // Initialize to -1, -1, which means no focus position on the grid on initial render.
  const [focusPosition, setFocusPosition] = useState<GridPosition>({ row: -1, col: -1 });
  const [selection, setSelection] = useState<GridSelection>(null);

  const logDebug = useCallback(
    (action: string, details?: unknown) => {
      if (debug) {
        console.log(`[KeyboardNavigation] ${action}`, JSON.stringify(details, null, 2));
      }
    },
    [debug],
  );

  const handleError = useCallback(
    (error: ValidationError) => {
      logDebug('Error', error);
      onError?.(error);
      if (strictValidation) {
        throw new Error(`[KeyboardNavigation] ${error.code}: ${error.message}`);
      }
    },
    [logDebug, onError, strictValidation],
  );

  const validatePosition = useCallback(
    (position: GridPosition, action: string, allowNegative = true) => {
      if (!isValidPosition(position, rowCount, columnCount, allowNegative)) {
        handleError({
          code: 'OUT_OF_BOUNDS',
          message: `Invalid position for ${action}: row=${position.row}, col=${position.col}`,
          details: { position, rowCount, columnCount },
        });
        return false;
      }
      return true;
    },
    [rowCount, columnCount, handleError],
  );

  const handleCellFocus = useCallback(
    (row: number, col: number) => {
      const position = { row, col };
      logDebug('Focus Request', { position });

      if (!validatePosition(position, 'focus', true)) {
        return;
      }

      setFocusPosition(position);
      onFocusChange?.(position);
    },
    [onFocusChange, validatePosition, logDebug],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, row: number, col: number) => {
      try {
        const position = { row, col };
        logDebug('KeyDown', {
          key: e.key,
          position,
          modifiers: {
            ctrl: e.ctrlKey,
            alt: e.altKey,
            shift: e.shiftKey,
            meta: e.metaKey,
          },
        });

        if (!validatePosition(position, 'keydown')) {
          return;
        }

        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onActivate?.(position);
          return;
        }

        if (e.key === 'Escape') {
          e.preventDefault();
          // Clear selection and grid focus position, return focus to table
          setSelection(null);
          const noFocus = { row: -1, col: -1 };
          setFocusPosition(noFocus);
          onFocusChange?.(noFocus);
          // Focus the table element to exit grid navigation mode
          if (tableRef.current) {
            tableRef.current.focus();
          }
          return;
        }

        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          // onKeyPress handler (Map3) checks selection state and dispatches to all selected cells
          onKeyPress?.('Delete', { row, col });
          return;
        }

        let newRow = row;
        let newCol = col;

        switch (e.key) {
          case 'ArrowUp':
            if (e.metaKey || e.ctrlKey) {
              // Command+Up (Mac) or Ctrl+Up (Windows): move to first row
              newRow = 0;
            } else {
              newRow = Math.max(0, row - 1);
            }
            break;
          case 'ArrowDown':
            if (e.metaKey || e.ctrlKey) {
              // Command+Down (Mac) or Ctrl+Down (Windows): move to last row
              newRow = rowCount - 1;
            } else {
              newRow = Math.min(rowCount - 1, row + 1);
            }
            break;
          case 'ArrowLeft':
            if (e.metaKey || e.ctrlKey) {
              // Command+Left (Mac) or Ctrl+Left (Windows): move to first column
              newCol = 0;
            } else {
              if (col === scrollEdges.left && canScrollBack) {
                e.preventDefault();
                scrollBack?.();
                return;
              }
              newCol = Math.max(0, col - 1);
            }
            break;
          case 'ArrowRight':
            if (e.metaKey || e.ctrlKey) {
              // Command+Right (Mac) or Ctrl+Right (Windows): move to last column
              newCol = columnCount - 1;
            } else {
              if (col === scrollEdges.right && canScrollForward) {
                e.preventDefault();
                scrollForward?.();
                return;
              }
              newCol = Math.min(columnCount - 1, col + 1);
            }
            break;
          case 'Tab':
            // Tab should behave like right arrow key, Shift+Tab like left arrow key
            if (e.shiftKey) {
              // Shift+Tab: behave like left arrow key
              if (col === scrollEdges.left && canScrollBack) {
                e.preventDefault();
                scrollBack?.();
                return;
              }
              newCol = Math.max(0, col - 1);
            } else {
              // Tab: behave like right arrow key
              if (col === scrollEdges.right && canScrollForward) {
                e.preventDefault();
                scrollForward?.();
                return;
              }
              newCol = Math.min(columnCount - 1, col + 1);
            }
            break;
          case 'Home':
            if (e.ctrlKey) {
              // Ctrl+Home: move to first cell in first row
              newRow = 0;
              newCol = 0;
            } else {
              // Home: move to first cell in current row
              newCol = 0;
            }
            break;
          case 'End':
            if (e.ctrlKey) {
              // Ctrl+End: move to last cell in last row
              newRow = rowCount - 2;
              newCol = columnCount - 1;
            } else {
              // End: move to last cell in current row
              newCol = columnCount - 1;
            }
            break;

          default:
            onKeyPress?.(e.key, { row, col });
            return;
        }

        e.preventDefault();
        handleCellFocus(newRow, newCol);

        // Focus the new cell
        const selector = `[data-row="${newRow}"][data-col="${newCol}"]`;
        const cell = tableRef.current?.querySelector(selector) as HTMLElement;
        cell?.focus();

        if (e.shiftKey && e.key !== 'Tab') {
          // Update selection end point when using arrow keys with shift
          // Clamp columns to >= 1 to exclude objective column (col 0) from selection
          const startCol = selection?.start?.col ?? col;
          const newSelection: GridSelection = {
            start: { row: selection?.start?.row ?? row, col: Math.max(1, startCol) },
            end: { row: newRow, col: Math.max(1, newCol) },
          };
          setSelection(newSelection);
        } else {
          // Clear selection when not using shift
          setSelection(null);
        }
      } catch (error) {
        const errorDetails = error instanceof Error ? error.message : 'Unknown error';
        handleError({
          code: 'INVALID_KEY_ACTION',
          message: 'Error handling keyboard event',
          details: { error: errorDetails },
        });

        // Reset focus position on error
        const noFocus = { row: -1, col: -1 };
        setFocusPosition(noFocus);
        onFocusChange?.(noFocus);
      }
    },
    [
      rowCount,
      columnCount,
      tableRef,
      handleCellFocus,
      onFocusChange,
      onActivate,
      canScrollForward,
      canScrollBack,
      scrollForward,
      scrollBack,
      scrollEdges,
      onKeyPress,
      logDebug,
      validatePosition,
      handleError,
      selection,
    ],
  );

  const handleSelectionStart = useCallback((row: number, col: number) => {
    // Clamp to col >= 1 to exclude objective column from selection
    const clampedCol = Math.max(1, col);
    setSelection({
      start: { row, col: clampedCol },
      end: { row, col: clampedCol },
    });
  }, []);

  const handleSelectionEnd = useCallback((row: number, col: number) => {
    // Clamp to col >= 1 to exclude objective column from selection
    const clampedCol = Math.max(1, col);
    setSelection((prev) => {
      if (prev) {
        return { ...prev, end: { row, col: clampedCol } };
      }
      // No prior selection — create one from the current focus position
      const startCol = Math.max(1, focusPosition.col);
      return {
        start: { row: focusPosition.row, col: startCol },
        end: { row, col: clampedCol },
      };
    });
  }, [focusPosition]);

  return {
    focusPosition,
    selection,
    handleCellFocus,
    handleKeyDown,
    handleSelectionStart,
    handleSelectionEnd,
  };
}
